import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { TwoFactorAuthForm } from '@pages/TwoFactorAuth/TwoFactorAuthForm'
import twoFaStyles from '@pages/TwoFactorAuth/TwoFactorAuthPage.module.scss'
import { useTwoFactorAuthText } from '@pages/TwoFactorAuth/useTwoFactorAuthText'

import { Body1, Heading6 } from '@ui/Typography'
import { RichIcon } from '@ui/components/RichIcon'
import { CheckIcon } from '@ui/icons/Check.icon'

import { useFundingNav } from '@hooks/useFundingNav'

import { Box } from '@shared/components/Box'
import { FundingTemplate } from '@shared/templates/FundingTemplate'

import type { WithdrawalTwoFactorLocationState } from '../WithdrawalFlowLayout/types'
import { useWithdrawalTwoFactor } from './hooks/useWithdrawalTwoFactor'

export const WithdrawalTwoFactorPage: React.FC = () => {
  const { t } = useTranslation()
  const contentRef = useRef<HTMLDivElement>(null)

  const { go, state } = useFundingNav()

  const locationState = state as WithdrawalTwoFactorLocationState | undefined
  const hasTwoFactor = (locationState?.transaction?.twoFactor?.length ?? 0) > 0

  const {
    isGoogleAuth,
    twoFactorData,
    error,
    status,
    resendError,
    disableResend,
    initialCountdown,
    setInitialCountdown,
    handleSubmitWithCode,
    resendCode,
  } = useWithdrawalTwoFactor(locationState)

  const { resendText, errorText } = useTwoFactorAuthText({
    twoFactor: twoFactorData,
    initialCountdown,
    resendCode,
    disableResend,
    resendError,
  })

  useEffect(() => {
    if (!locationState?.transaction || !hasTwoFactor) {
      go('../amount', { replace: true })
    }
  }, [hasTwoFactor, locationState, go])

  useEffect(() => {
    if (initialCountdown <= 0) return
    const interval = setInterval(() => {
      setInitialCountdown((prev: number) => {
        if (prev <= 1) {
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [initialCountdown, setInitialCountdown])

  if (!locationState?.transaction || !hasTwoFactor) return null

  return (
    <FundingTemplate contentRef={contentRef} onBack={() => go(-1)} maxWidth={456}>
      <Box
        flex
        direction='column'
        gap='x16'
        fullWidth
        align='center'
        flexItem='1 1 auto'
        minHeight='100%'
      >
        <Box flex align='center' gap='x8'>
          <Heading6 medium>{t("Confirm it's you")}</Heading6>
          {status === 'success' && (
            <RichIcon
              bg='surface-secondary-high-em'
              icon={{ icon: CheckIcon, color: 'text-white' }}
              shape='circle'
              size='x24'
            />
          )}
        </Box>
        {!isGoogleAuth && twoFactorData?.authMedium && (
          <Body1>
            {t('Sign up.According to your security settings, a verification code is sent to')}{' '}
            <span className={twoFaStyles.email}>{twoFactorData.authMedium}</span>
          </Body1>
        )}
        {isGoogleAuth && (
          <Body1>{t('Sign up.Enter the 6-digit code from your Google Authenticator app')}</Body1>
        )}
        <TwoFactorAuthForm
          resendText={resendText}
          errorText={errorText}
          openRecoveryModal={() => {}}
          statusType={status}
          onSubmit={handleSubmitWithCode}
          validationError={error}
          autoFocus
          withConfirmButton
          stickyConfirmButton
          contentRef={contentRef}
        />
      </Box>
    </FundingTemplate>
  )
}
